import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { BrowserStorage } from '@app/models/browserStorage';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  constructor(private angularFireAuth: AngularFireAuth, private router: Router) {}

  /**
   * Reads the current Firebase ID token ONCE.
   *
   * `take(1)` is load-bearing — do not drop it. `AngularFireAuth.idToken` is cold and not
   * multicast (`idToken = user.pipe(switchMap(u => u ? from(u.getIdToken()) : of(null)))`), so
   * every subscription registers its own `onIdTokenChanged` listener and keeps it alive until
   * unsubscribed. This method previously subscribed without ever tearing the subscription down,
   * which self-fed the `else` branch below: with no user signed in, `idToken` emits null →
   * `signOut()` → Firebase notifies its ID-token listeners → the still-open subscription re-runs
   * → `signOut()` again → forever. Firestore re-authenticates its Listen webchannel on every
   * auth-credential change, so that loop flooded
   * `firestore.googleapis.com/.../Listen/channel` with reconnects — most visibly on the login
   * page, where APP_INITIALIZER calls this with no token yet.
   *
   * Matches the one-shot pattern already used in `AuthService.initializeAuth()`.
   */
  async getFirebaseToken(): Promise<string> {
    const token = await this.angularFireAuth.idToken.pipe(take(1)).toPromise();
    if (token) {
      BrowserStorage.set('userToken', token);
      return token;
    }
    BrowserStorage.delete('userToken');
    await this.angularFireAuth.signOut();
    return token;
  }
}
